import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Copy, Check, Code, Key, Zap, Shield } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

export default function ChatbotApiDocs() {
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const endpoints = [
    {
      method: "POST",
      path: "/functions/v1/chatbot-api",
      description: "Sendet eine Nachricht an den Chatbot und erhält eine KI-generierte Antwort.",
      request: `{
  "apiKey": "kt_chat_xxxxx...",
  "message": "Was sind Ihre Öffnungszeiten?",
  "sessionId": "optional_session_id",
  "conversationHistory": []
}`,
      response: `{
  "response": "Wir sind Mo-Fr von 9-18 Uhr für Sie da.",
  "sessionId": "session_1234567890",
  "config": {
    "botName": "KI-Assistent",
    "primaryColor": "#0066cc",
    "showBranding": true
  }
}`
    },
    {
      method: "POST",
      path: "/functions/v1/chatbot-request-callback",
      description: "Speichert eine Rückrufanfrage von einem Website-Besucher (Basic+ Plan).",
      request: `{
  "apiKey": "kt_chat_xxxxx...",
  "sessionId": "session_1234567890",
  "name": "Max Mustermann",
  "phone": "+49 123 456789",
  "preferredTime": "Vormittags",
  "message": "Frage zu Ihren Services"
}`,
      response: `{
  "success": true,
  "message": "Callback request submitted successfully",
  "requestId": "uuid-here"
}`
    },
    {
      method: "POST",
      path: "/functions/v1/chatbot-submit-lead",
      description: "Speichert Kontaktdaten eines Website-Besuchers (Basic+ Plan).",
      request: `{
  "apiKey": "kt_chat_xxxxx...",
  "sessionId": "session_1234567890",
  "name": "Max Mustermann",
  "email": "max@beispiel.de",
  "phone": "+49 123 456789",
  "message": "Interesse an Ihren Leistungen"
}`,
      response: `{
  "success": true,
  "message": "Contact information submitted successfully",
  "leadId": "uuid-here"
}`
    },
    {
      method: "POST",
      path: "/functions/v1/chatbot-update-config",
      description: "Aktualisiert die Bot-Konfiguration (Pro+ für Branding-Optionen).",
      request: `{
  "apiKey": "kt_chat_xxxxx...",
  "config": {
    "bot_name": "Mein Assistent",
    "welcome_message": "Hallo! Wie kann ich helfen?",
    "primary_color": "#0066cc",
    "callback_enabled": true
  }
}`,
      response: `{
  "success": true,
  "message": "Configuration updated successfully"
}`
    }
  ];

  const codeExamples = {
    javascript: `// JavaScript / Node.js
const response = await fetch('https://ydwqhnwvmsghmfdbgwub.supabase.co/functions/v1/chatbot-api', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    apiKey: 'kt_chat_xxxxx...',
    message: 'Hallo, ich habe eine Frage.',
    sessionId: sessionStorage.getItem('chatSessionId')
  })
});

const data = await response.json();
console.log(data.response);`,

    python: `# Python
import requests

response = requests.post(
    'https://ydwqhnwvmsghmfdbgwub.supabase.co/functions/v1/chatbot-api',
    json={
        'apiKey': 'kt_chat_xxxxx...',
        'message': 'Hallo, ich habe eine Frage.',
        'sessionId': None  # Optional
    }
)

data = response.json()
print(data['response'])`,

    curl: `# cURL
curl -X POST \\
  'https://ydwqhnwvmsghmfdbgwub.supabase.co/functions/v1/chatbot-api' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "apiKey": "kt_chat_xxxxx...",
    "message": "Hallo, ich habe eine Frage."
  }'`
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card/50 backdrop-blur sticky top-0 z-10">
        <div className="container mx-auto max-w-6xl px-4 py-4 flex items-center gap-4">
          <Link to="/chatbot-produkt">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Zurück
            </Button>
          </Link>
          <div>
            <h1 className="text-xl font-bold">Chatbot API Dokumentation</h1>
            <p className="text-sm text-muted-foreground">Premium Plan Feature</p>
          </div>
        </div>
      </header>

      <main className="container mx-auto max-w-6xl px-4 py-8 space-y-12">
        {/* Overview */}
        <section>
          <h2 className="text-2xl font-bold mb-6">Übersicht</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="pt-6">
                <Key className="w-10 h-10 mb-4 text-primary" />
                <h3 className="font-semibold mb-2">API-Key Authentifizierung</h3>
                <p className="text-sm text-muted-foreground">
                  Jede Anfrage erfordert Ihren API-Key im Request-Body.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <Zap className="w-10 h-10 mb-4 text-primary" />
                <h3 className="font-semibold mb-2">RESTful JSON API</h3>
                <p className="text-sm text-muted-foreground">
                  Einfache HTTP POST Requests mit JSON-Payloads.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <Shield className="w-10 h-10 mb-4 text-primary" />
                <h3 className="font-semibold mb-2">DSGVO-konform</h3>
                <p className="text-sm text-muted-foreground">
                  Alle Daten werden in Deutschland verarbeitet und gespeichert.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Widget Integration */}
        <section>
          <h2 className="text-2xl font-bold mb-4">Widget-Integration</h2>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Einfache Einbindung per Script-Tag</CardTitle>
              <CardDescription>
                Fügen Sie diesen Code vor dem schließenden &lt;/body&gt; Tag Ihrer Website ein.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="relative">
                <pre className="bg-muted rounded-lg p-4 overflow-x-auto text-sm">
                  <code>{`<script 
  src="https://ydwqhnwvmsghmfdbgwub.supabase.co/functions/v1/chatbot-widget-js"
  data-api-key="IHR_API_KEY">
</script>`}</code>
                </pre>
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={() => copyToClipboard(`<script src="https://ydwqhnwvmsghmfdbgwub.supabase.co/functions/v1/chatbot-widget-js" data-api-key="IHR_API_KEY"></script>`, 'widget')}
                >
                  {copiedCode === 'widget' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                Ersetzen Sie <code className="bg-muted px-1 rounded">IHR_API_KEY</code> mit Ihrem API-Key aus dem Admin-Bereich.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* Base URL */}
        <section>
          <h2 className="text-2xl font-bold mb-4">Basis-URL</h2>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between bg-muted rounded-lg p-4">
                <code className="text-sm font-mono">
                  https://ydwqhnwvmsghmfdbgwub.supabase.co
                </code>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard('https://ydwqhnwvmsghmfdbgwub.supabase.co', 'baseurl')}
                >
                  {copiedCode === 'baseurl' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Code Examples */}
        <section>
          <h2 className="text-2xl font-bold mb-6">Code-Beispiele</h2>
          <div className="space-y-4">
            {Object.entries(codeExamples).map(([lang, code]) => (
              <Card key={lang}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base capitalize flex items-center gap-2">
                      <Code className="w-4 h-4" />
                      {lang}
                    </CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(code, lang)}
                    >
                      {copiedCode === lang ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <pre className="bg-muted rounded-lg p-4 overflow-x-auto text-sm">
                    <code>{code}</code>
                  </pre>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Endpoints */}
        <section>
          <h2 className="text-2xl font-bold mb-6">Endpoints</h2>
          <div className="space-y-6">
            {endpoints.map((endpoint, i) => (
              <Card key={i}>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <span className="px-2 py-1 bg-primary text-primary-foreground text-xs font-mono rounded">
                      {endpoint.method}
                    </span>
                    <code className="text-sm font-mono">{endpoint.path}</code>
                  </div>
                  <CardDescription className="mt-2">{endpoint.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className="text-sm font-semibold mb-2">Request Body</h4>
                    <div className="relative">
                      <pre className="bg-muted rounded-lg p-4 overflow-x-auto text-sm">
                        <code>{endpoint.request}</code>
                      </pre>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => copyToClipboard(endpoint.request, `req-${i}`)}
                      >
                        {copiedCode === `req-${i}` ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold mb-2">Response</h4>
                    <pre className="bg-green-50 dark:bg-green-950/30 rounded-lg p-4 overflow-x-auto text-sm">
                      <code>{endpoint.response}</code>
                    </pre>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Error Handling */}
        <section>
          <h2 className="text-2xl font-bold mb-6">Fehlerbehandlung</h2>
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 text-xs font-mono rounded">
                    400
                  </span>
                  <div>
                    <p className="font-medium">Bad Request</p>
                    <p className="text-sm text-muted-foreground">Fehlende oder ungültige Parameter im Request.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 text-xs font-mono rounded">
                    401
                  </span>
                  <div>
                    <p className="font-medium">Unauthorized</p>
                    <p className="text-sm text-muted-foreground">Ungültiger oder inaktiver API-Key.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 text-xs font-mono rounded">
                    403
                  </span>
                  <div>
                    <p className="font-medium">Forbidden</p>
                    <p className="text-sm text-muted-foreground">Feature nicht im aktuellen Plan verfügbar oder Domain nicht autorisiert.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <span className="px-2 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 text-xs font-mono rounded">
                    429
                  </span>
                  <div>
                    <p className="font-medium">Too Many Requests</p>
                    <p className="text-sm text-muted-foreground">Tägliches oder monatliches Nachrichtenlimit erreicht.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 text-xs font-mono rounded">
                    500
                  </span>
                  <div>
                    <p className="font-medium">Internal Server Error</p>
                    <p className="text-sm text-muted-foreground">Interner Serverfehler. Bitte später erneut versuchen.</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Rate Limits */}
        <section>
          <h2 className="text-2xl font-bold mb-6">Rate Limits</h2>
          <Card>
            <CardContent className="pt-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 font-semibold">Plan</th>
                    <th className="text-left py-2 font-semibold">Limit</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="py-2">Free</td>
                    <td className="py-2">10 Nachrichten / Tag</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2">Basic</td>
                    <td className="py-2">150 Nachrichten / Monat</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2">Pro</td>
                    <td className="py-2">750 Nachrichten / Monat</td>
                  </tr>
                  <tr>
                    <td className="py-2">Premium</td>
                    <td className="py-2">2.000 Nachrichten / Monat</td>
                  </tr>
                </tbody>
              </table>
            </CardContent>
          </Card>
        </section>

        {/* Support */}
        <section className="text-center py-8">
          <h2 className="text-2xl font-bold mb-4">Fragen?</h2>
          <p className="text-muted-foreground mb-6">
            Kontaktieren Sie uns für technischen Support oder individuelle Integrationen.
          </p>
          <Button asChild>
            <a href="mailto:kontakt@klartech-mueritz.de">Support kontaktieren</a>
          </Button>
        </section>
      </main>
    </div>
  );
}
